import React, { useCallback } from 'react'
import { useDispatch } from 'react-redux'
import { useForm } from 'react-hook-form'
import * as yup from 'yup'
import { yupResolver } from '@hookform/resolvers/yup'
import { bool } from 'prop-types'
import ModalBtns from '@groovehq/internal-design-system/lib/components/ModalBtns/ModalBtns'
import { AdminAccessDrawer } from 'subapps/settings/components/drawers/NoAccess'
import HeaderWithImage from '@groovehq/internal-design-system/lib/components/HeaderWithImage/HeaderWithImage'
import Textarea from '@groovehq/internal-design-system/lib/components/Textarea/Textarea'
import Switch from '@groovehq/internal-design-system/lib/components/Switch/Switch'
import { styles as fieldStyles } from '@groovehq/internal-design-system/lib/components/Field/Field.styles'
import { text } from '@groovehq/internal-design-system/lib/styles/elements'
import { DRAWER_TYPE_AGENT_ADD_MAILBOX_ACCESS } from 'ducks/drawers/types'
import { useDrawer } from 'ducks/drawers/hooks'
import {
  doClearAllAgentDrafts,
  doSaveAgentDrafts,
} from 'ducks/agents/operations'
import { pluralize } from 'util/strings'
import { useConfirmHoldsCallback, useRhfDirtyHold } from 'util/dirtyHolds'
import { convertEmailsToAgents } from 'ducks/agents/utils'
import { styles } from './styles'

const BULK_FORM_SCHEMA = yup.object().shape({
  bulkEmails: yup
    .string()
    .trim()
    .lowercase()
    .required(`${app.t('Agents')} emails are required`),
  bulkRoleIsAdmin: yup.boolean().required('Role is required'),
})

const AgentAddDrawer = ({
  open,
  onClose,
  onExit,
  drawerId,
  drawerResourceId,
  queryId,
}) => {
  const dispatch = useDispatch()

  const { openDrawer: openAddMailboxAccessDrawer } = useDrawer(
    DRAWER_TYPE_AGENT_ADD_MAILBOX_ACCESS
  )

  const {
    handleSubmit,
    formState: { isValid, isDirty, errors },
    register,
    setError,
    control,
  } = useForm({
    mode: 'all',
    resolver: yupResolver(BULK_FORM_SCHEMA),
    defaultValues: {
      bulkRoleIsAdmin: false,
      bulkEmails: '',
    },
  })

  const { releaseHold, holdKey } = useRhfDirtyHold(drawerId, control)

  const beforeDrawerClose = useCallback(
    () => {
      dispatch(doClearAllAgentDrafts())
    },
    [dispatch]
  )

  const handleOnClose = useConfirmHoldsCallback(
    holdKey,
    () => {
      beforeDrawerClose()
      onClose()
    },
    [onClose, beforeDrawerClose]
  )

  const handleOnExit = useConfirmHoldsCallback(
    null,
    () => {
      beforeDrawerClose()
      onExit()
    },
    [onExit, beforeDrawerClose]
  )

  const onSubmit = useCallback(
    data => {
      const { bulkEmails, bulkRoleIsAdmin } = data
      const { users, splitBulkEmails } = convertEmailsToAgents(bulkEmails, {
        bulkRoleIsAdmin,
        agentIdPrefix: drawerResourceId,
      })
      if (users.length > 0 && users.length === splitBulkEmails.length) {
        dispatch(doSaveAgentDrafts(users))
        releaseHold()
        openAddMailboxAccessDrawer('new', {
          additionalProps: {
            queryId,
            userIdsToInvite: users.map(u => u.id),
          },
        })
      } else {
        setError('bulkEmails', {
          type: 'manual',
          message: `Invalid ${app.t('agent')} email ${pluralize(
            splitBulkEmails.length,
            'address',
            'es'
          )} detected`,
        })
      }
    },
    [
      dispatch,
      drawerResourceId,
      openAddMailboxAccessDrawer,
      queryId,
      setError,
      releaseHold,
    ]
  )

  const DrawerForm = useCallback(
    subprops => <form onSubmit={handleSubmit(onSubmit)} {...subprops} />,
    [handleSubmit, onSubmit]
  )

  const footer = (
    <ModalBtns
      saveBtnDisabled={!isValid || !isDirty}
      saveBtnText="Next"
      // type="submit" will automatically trigger the form submit event when clicked
      saveBtnHtmlType="submit"
      tertiaryBtnText="Cancel"
      onClickTertiaryBtn={handleOnClose}
    />
  )

  return (
    <AdminAccessDrawer
      title={`Add ${app.t('agents')}`}
      open={open}
      onClose={handleOnExit}
      footer={footer}
      container={DrawerForm}
    >
      <HeaderWithImage
        intro="Enter emails separated by commas, tabs or new lines."
        className="grui mb-12 mt-12"
      />
      <div>
        <Textarea
          {...register('bulkEmails')}
          className="grui my-7"
          css={styles.textArea}
          rows="10"
          placeholder={`name@email.com\nname@email.com\nname@email.com`}
          validateStatus={errors?.bulkEmails ? 'error' : undefined}
          help={errors?.bulkEmails?.message}
        />
      </div>
      <div className="grui mt-12">
        <span css={[fieldStyles.labelBox, text.styles.textSm]}>
          Set as admin
        </span>
        <Switch
          id="roleSelection"
          name="roleSelection"
          {...register('bulkRoleIsAdmin')}
        >
          Admins can add new {app.t('agents')} and edit {app.t('mailbox')} and
          company settings.
        </Switch>
      </div>
    </AdminAccessDrawer>
  )
}

AgentAddDrawer.propTypes = {
  open: bool.isRequired,
}

AgentAddDrawer.defaultProps = {}

export default AgentAddDrawer
